import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import momentBusinessDays from "moment-business-days";
import { StatusCodes } from "http-status-codes";
import { Alert, AutoComplete, Button, Calendar, Checkbox, Col, Form, Row, Select, Typography } from "antd";
import { get, map } from "lodash";
import { PlusSquareOutlined } from "@ant-design/icons";
import EmptyTimeslot from "../Timeslots/EmptyTimeslot";
import Timeslot from "../Timeslots/Timeslot";
import TimeslotModal from "../TimeslotsModal";
import "./index.css";
import { createSpecialistAvailableTimeslots, syncSpecialists, updateSpecialist } from "../../../../api/specialists";
import { STATUS, TIME_FORMAT } from "../../../../constants";
import { alertMessageMapping } from "./constants";
import AvailableTimeTable from "../AvailableTimeTable";
import isEqual from "lodash/isEqual";
import { toValidArray } from "../../../../utils/validateArray";
import { useDispatch, useSelector } from "react-redux";
import { INITIAL_AND_REVIEW_APT_LENGTH_LIST, TIMEZONE } from "../../constants";
import RepeatTimeForm from "../RepeatTimeForm";
import { fetchSpecialists, selectActiveSpecialists } from "../../../../components/appSlice";

const { Option } = Select;
const { Title } = Typography;
const { DATE: DATE_FORMAT, MONTH: MONTH_FORMAT } = TIME_FORMAT;

let isPanelChange = false;

momentBusinessDays.updateLocale("aus", {
  workingWeekdays: [1, 2, 3, 4, 5],
});

const defaultStatus = {
  status: STATUS.DEFAULT,
  message: null,
};

const LAST_DAY_OF_YEAR = moment().clone().endOf("year");

const defaultRepeatedScheduleInfo = {
  repeatedWeeks: 1,
  startDate: moment(),
  endDate: LAST_DAY_OF_YEAR,
  daysOfWeek: [],
};

const ScheduleForm = () => {
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [selectedSpecialistId, setSelectedSpecialistId] = useState();
  const [timezone, setTimezone] = useState();
  const [isPrivate, setIsPrivate] = useState(false);
  const [APTLength, setAPTLength] = useState();
  const [timeslots, setTimeslots] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [createStatus, setCreateStatus] = useState(defaultStatus);
  const [repeatedScheduleInfo, setRepeatedScheduleInfo] = useState(defaultRepeatedScheduleInfo);
  const [latestSelectedMoment, setLatestSelectedMoment] = useState(moment());
  const [syncSpecialistsStatus, setSyncSpecialistsStatus] = useState(STATUS.DEFAULT);
  const [isOneOfSchedule, setIsOneOfSchedule] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const dispatch = useDispatch();
  const rawSpecialists = useSelector(selectActiveSpecialists);
  const specialists = useMemo(() => {
    return Object.values(rawSpecialists || {}).map((specialist) => {
      const newTime = get(specialist, "appointmentLength.newTime", null);
      const reviewTime = get(specialist, "appointmentLength.reviewTime", null);
      return newTime && reviewTime
        ? {
            ...specialist,
            appointmentLength: { ...specialist.appointmentLength, APTLength: `${newTime}-${reviewTime}` },
          }
        : specialist;
    });
  }, [rawSpecialists]);

  const specialistOptions = useMemo(
    () =>
      specialists?.map((specialist) => ({
        key: specialist.id,
        ...specialist,
        value: `${specialist.firstName} ${specialist.lastName}`,
      })),
    [specialists]
  );

  const arrValues = useMemo(() => toValidArray(selectedDays), [selectedDays]);

  useEffect(() => {
    setCreateStatus({
      status: STATUS.DEFAULT,
      message: null,
    });
  }, [selectedSpecialistId, timezone, selectedDays, timeslots]);

  const resetPage = () => {
    setSelectedSpecialistId();
    setAPTLength();
    setTimezone();
    setTimeslots([]);
    setIsPrivate(false);
    setSelectedDays([]);
    setRepeatedScheduleInfo(defaultRepeatedScheduleInfo);
  };

  const resetCalendar = () => {
    setSelectedDays([]);
    setTimeslots([]);
    setRepeatedScheduleInfo(defaultRepeatedScheduleInfo);
    setIsPrivate(false);
  };

  const onSelect = useCallback(
    (selected) => {
      if (selected.format(MONTH_FORMAT) !== latestSelectedMoment.format(MONTH_FORMAT)) {
        setLatestSelectedMoment(selected);
      }
      if (isPanelChange) {
        isPanelChange = false;
        return;
      }
      const index = arrValues.findIndex((e) => isEqual(e, moment(selected).format(DATE_FORMAT)));
      const temp = [...arrValues];

      if (index !== -1) {
        temp.splice(index, 1);
      } else {
        temp.push(moment(selected).format(DATE_FORMAT));
      }

      setSelectedDays(temp);
    },
    [arrValues, setSelectedDays, latestSelectedMoment]
  );

  const deleteTimeslot = (currentId) => {
    setTimeslots(timeslots.filter(({ id }) => id !== currentId));
  };

  const validate = () => {
    if (!selectedSpecialistId) {
      return "Please select a specialist";
    }

    // Sync can deactivate the selected specialist after they were picked.
    if (!specialists.some(({ id }) => id === selectedSpecialistId)) {
      return "This specialist is no longer active. Please select another specialist";
    }

    if (!timezone) {
      return "Please select a timezone";
    }

    if (!APTLength) {
      return "Please select appointment length";
    }

    if (selectedDays.length < 1) {
      return "Please select at least one date";
    }

    if (timeslots.length < 1) {
      return "Please select at least one timeslot";
    }

    return null;
  };

  const openTimeslotModal = () => {
    if (selectedDays.length < 1) {
      setCreateStatus({
        status: STATUS.FAILED,
        message: "Please select at least one date",
      });
      return;
    }

    setIsTimeModalOpen(true);
  };

  const onFinish = async () => {
    const error = validate();
    if (error) {
      setCreateStatus({
        status: STATUS.FAILED,
        message: error,
      });
      return;
    }

    const specialistAvailableDate = {
      dates: selectedDays,
      timezone,
      specialistId: selectedSpecialistId,
      timeslots,
    };
    setIsSavingChanges(true);
    await createSpecialistAvailableTimeslots(specialistAvailableDate, setCreateStatus, isPrivate);

    const [newTime, reviewTime] = APTLength.split("-");
    const specialist = specialists.find(({ id }) => id === selectedSpecialistId);
    const newSpecialist = {
      specialist,
      appointmentLength: {
        timezone,
        newTime,
        reviewTime,
      },
    };
    await updateSpecialist(newSpecialist, () => {});
    dispatch(fetchSpecialists());
    resetCalendar();
    setIsSavingChanges(false);
  };

  const resetStatus = () => {
    setCreateStatus(defaultStatus);
  };

  const customRenderDate = useCallback(
    (current) => {
      if (arrValues.some((e) => isEqual(e, moment(current).format(DATE_FORMAT)))) {
        return <div className={"selectedDate"}>{current.format("DD")}</div>;
      }
      if (current.format(DATE_FORMAT) === moment().format(DATE_FORMAT)) {
        return <div className={"today"}>{current.format("DD")}</div>;
      }
      return <div>{current.format("DD")}</div>;
    },
    [arrValues]
  );

  const onSpecialistChange = (input, specialist) => {
    const APTLength = get(specialist, "appointmentLength.APTLength", null);
    const timezone = get(specialist, "appointmentLength.timezone", null);
    if (selectedSpecialistId) {
      resetCalendar();
    }
    setSelectedSpecialistId(specialist.id);
    setTimezone(timezone);
    if (APTLength && map(INITIAL_AND_REVIEW_APT_LENGTH_LIST, "value").includes(APTLength)) {
      setAPTLength(APTLength);
    } else {
      setAPTLength(null);
    }
  };

  const handleSyncSpecialists = async () => {
    setSyncSpecialistsStatus(STATUS.LOADING);
    try {
      const res = await syncSpecialists();
      if (res.status === StatusCodes.OK) {
        dispatch(fetchSpecialists());
        setSyncSpecialistsStatus(STATUS.SUCCEED);
      }
    } catch (e) {
      setSyncSpecialistsStatus(STATUS.FAILED);
    }
  };

  return (
    <Form onFinish={onFinish}>
      <Title level={2}>Specialist</Title>
      <Row gutter={16}>
        <Col span={9}>
          <Form.Item required={true} label="Specialist">
            <AutoComplete
              options={specialistOptions}
              allowClear
              filterOption={(inputValue, option) => option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
              loading={false}
              onSelect={onSpecialistChange}
              onChange={resetPage}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Button loading={syncSpecialistsStatus === STATUS.LOADING} onClick={handleSyncSpecialists}>
            Sync Specialists
          </Button>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={9}>
          <Form.Item label="Timezone" required={true}>
            <Select onChange={(value) => setTimezone(value)} value={timezone}>
              {TIMEZONE.map((timezone, index) => (
                <Option value={timezone} key={index}>
                  {timezone}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="New/Review APT length" required={true}>
            <Select onChange={(value) => setAPTLength(value)} value={APTLength} allowClear>
              {INITIAL_AND_REVIEW_APT_LENGTH_LIST.map(({ label, value }, index) => (
                <Option value={value} key={index}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Title level={2} className="schedule-title">
        Schedule
        <Checkbox
          checked={isOneOfSchedule}
          onChange={(e) => {
            setIsOneOfSchedule(e.target.checked);
          }}
          className="one-of-schedule-checkbox"
        >
          One off schedule
        </Checkbox>
      </Title>
      <Row justify="start">
        <Col span={8} className="calendar-container">
          <Title level={5}>
            Calendar
            <Button type="primary" onClick={resetCalendar} className="reset-calendar-button" size={"small"}>
              Reset
            </Button>
          </Title>
          <Form.Item wrapperCol={{ span: 20 }}>
            <div className="calendar-card">
              <Calendar
                onSelect={onSelect}
                defaultValue={moment()}
                fullscreen={false}
                onPanelChange={() => (isPanelChange = true)}
                dateFullCellRender={customRenderDate}
              />
            </div>
          </Form.Item>
        </Col>
        <Col span={8} className="timeslots-container">
          <Title level={5}>
            Timeslots
            <PlusSquareOutlined onClick={openTimeslotModal} className="timeslots-icon" />
          </Title>
          {timeslots.length < 1 ? (
            <EmptyTimeslot handleClick={openTimeslotModal} />
          ) : (
            timeslots.map(({ start, end, selectedRoom }, index) => (
              <Timeslot
                start={start}
                end={end}
                selectedRoom={selectedRoom}
                key={index}
                handleDelete={() => deleteTimeslot(`${start}-${end}`)}
              />
            ))
          )}
          {!isOneOfSchedule && (
            <Form.Item>
              <RepeatTimeForm
                isPrivate={isPrivate}
                setIsPrivate={setIsPrivate}
                setSelectedDays={setSelectedDays}
                repeatedScheduleInfo={repeatedScheduleInfo}
                setRepeatedScheduleInfo={setRepeatedScheduleInfo}
              />
            </Form.Item>
          )}
        </Col>
        <Col span={8} className="available-time-container">
          <Title level={5}>Availabilities</Title>
          <AvailableTimeTable
            timezone={timezone}
            createStatus={createStatus}
            specialistId={selectedSpecialistId}
            latestSelectedMoment={latestSelectedMoment}
          />
        </Col>
      </Row>

      {alertMessageMapping[createStatus.status] && (
        <Alert
          message={createStatus.message || alertMessageMapping[createStatus.status].message}
          type={alertMessageMapping[createStatus.status].type}
        />
      )}

      <Form.Item wrapperCol={{ span: 16 }}>
        <Button loading={isSavingChanges} type="primary" htmlType="submit" className="save-button">
          Save
        </Button>
      </Form.Item>

      <TimeslotModal
        timeslots={timeslots}
        setTimeslots={setTimeslots}
        isTimeModalOpen={isTimeModalOpen}
        toggleModal={setIsTimeModalOpen}
        resetStatus={resetStatus}
      />
    </Form>
  );
};

export default ScheduleForm;
